import React from 'react';
import { Typography, IconButton, Button, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import type { HealthRecord, HealthData } from '../../services/lifeDataService';

/* ── color helper (same as globe marker) ──────────────────────────────────── */
function healthColor(record: HealthRecord): string {
  if (record.type === '血糖' && record.values[0] != null) {
    const v = record.values[0];
    if (v > 200) return '#ef5350';
    if (v >= 126) return '#ffa726';
    return '#66bb6a';
  }
  if (record.type === '血壓' && record.values[0] != null) {
    return record.values[0] > 140 ? '#ef5350' : '#66bb6a';
  }
  return '#42a5f5';
}

function formatValues(record: HealthRecord, config?: HealthData['config']): string {
  const typeConfig = config?.types.find(t => t.name === record.type);
  if (!typeConfig || typeConfig.valueSchema.length === 0) return '';
  if (typeConfig.valueSchema.length >= 3) {
    const [s, d, h] = record.values;
    return `${s ?? '?'}/${d ?? '?'} HR${h ?? '?'}`;
  }
  return record.values.map((v, i) => {
    const schema = typeConfig.valueSchema[i];
    return schema ? `${v} ${schema.unit}` : String(v);
  }).join(' / ');
}

interface Props {
  record: HealthRecord;
  healthConfig: HealthData['config'];
  position: { x: number; y: number };
  onClose: () => void;
  onEdit: (record: HealthRecord) => void;
}

const HealthSummaryCard: React.FC<Props> = ({ record, healthConfig, position, onClose, onEdit }) => {
  const isZh = navigator.language.startsWith('zh');
  const color = healthColor(record);
  const valStr = formatValues(record, healthConfig);
  const time = record.ts.slice(11, 16);

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        left: position.x + 12,
        top: position.y - 10,
        zIndex: 1200,
        borderRadius: 3,
        minWidth: 220,
        maxWidth: 280,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
        border: `2px solid ${color}66`,
      }}
    >
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${color}22 0%, rgba(255,255,255,0.95) 100%)`,
        padding: '10px 14px 8px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: `1px solid ${color}33`,
      }}>
        <span style={{ fontSize: 20 }}>🩺</span>
        <div style={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1a237e', lineHeight: 1 }}>
            {record.type}
            {record.condition ? ` · ${record.condition}` : ''}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#9e9e9e', mt: 0.3 }}>
            {time}
            {record.timezone ? `  ${record.timezone.replace('Asia/', '')}` : ''}
          </Typography>
        </div>
        <IconButton size="small" onClick={onClose}
          sx={{ color: '#bdbdbd', '&:hover': { color: '#555' }, p: 0.3 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </div>

      {/* Values */}
      {valStr && (
        <div style={{ padding: '10px 14px 6px' }}>
          <div style={{
            display: 'inline-block',
            background: `${color}18`,
            border: `1px solid ${color}55`,
            borderRadius: 8, padding: '4px 12px',
          }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.2 }}>
              {valStr}
            </Typography>
          </div>
        </div>
      )}

      {/* Location */}
      {record.location && (
        <div style={{ padding: '4px 14px 2px' }}>
          <Typography sx={{ fontSize: 12, color: '#607d8b' }}>
            📍 {record.location.placeName}
            {record.location.country ? ` · ${record.location.country}` : ''}
          </Typography>
        </div>
      )}

      {/* Food + medicine */}
      {(record.food || record.medicine) && (
        <div style={{ padding: '2px 14px' }}>
          <Typography sx={{ fontSize: 12, color: '#7986cb' }}>
            {[record.food, record.medicine].filter(Boolean).join('  ·  ')}
          </Typography>
        </div>
      )}

      {/* Notes */}
      {record.notes && (
        <div style={{ padding: '2px 14px 4px' }}>
          <Typography sx={{ fontSize: 12, color: '#90a4ae' }}>{record.notes}</Typography>
        </div>
      )}

      {/* Edit button */}
      <div style={{
        padding: '8px 14px 10px',
        display: 'flex', justifyContent: 'flex-end',
        borderTop: '1px solid #f5f5f5', marginTop: 4,
      }}>
        <Button
          size="small"
          startIcon={<EditIcon sx={{ fontSize: 14 }} />}
          onClick={() => onEdit(record)}
          sx={{
            color: color, fontSize: 12, fontWeight: 600,
            '&:hover': { background: `${color}15` },
          }}
        >
          {isZh ? '編輯' : 'Edit'}
        </Button>
      </div>
    </Paper>
  );
};

export default HealthSummaryCard;
